import { useState, useEffect, useRef, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { blueColor, primaryColor } from "../../../styled/Theme";
import { Button } from "antd";
import { Link, useNavigate } from "react-router-dom";
import CIcon from "@coreui/icons-react";
import { cilMenu } from "@coreui/icons";
import {
  HighlightAltOutlined,
  KeyboardArrowDown,
  PublicOutlined,
} from "@mui/icons-material";
import { URL_LOGIN, URL_REGISTER } from "../../../../utils/urls";
import { GenericDrawer } from "../../../common/GenericDrawer";
import { LogoHorizontalSVG } from "../../../../assets/svg/SvgIcons";
import { APP_NAME } from "../../../../utils/constants";
import { useDynamicPageTitle } from "../../../../hooks/dynamic_title/useDynamicPageTitle";
import { flattenMenu } from "../../../../utils/menuUtils";
import {
  UserOutlined,
  ShopOutlined,
  BankOutlined,
  ApartmentOutlined,
  PayCircleOutlined,
  CarOutlined,
  ShoppingOutlined,
  CalculatorOutlined,
  CreditCardOutlined,
  ReadOutlined,
  StarOutlined,
  MobileOutlined,
  QuestionCircleOutlined,
  TeamOutlined,
  EyeOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";

const goldColor = "#c9920a";
const NAV_HEIGHT = 64;
// Notch dimensions
const NOTCH_W = 18; // total width (half = 9px each side)
const NOTCH_H = 10; // height of the triangle

const navLinks = [
  { label: "Home", path: "/" },
  {
    label: "Solutions",
    path: "/solutions",
    children: [
      {
        label: "Overview",
        path: "/solutions#overview",
        icon: <HighlightAltOutlined />,
        desc: "Get to know how we make your financial life better",
      },
      {
        label: "Individuals",
        path: "/solutions#individuals",
        icon: <UserOutlined />,
        desc: "Personal payments & transfers",
      },
      {
        label: "Agents & Merchants",
        path: "/solutions#agents-merchants",
        icon: <ShopOutlined />,
        desc: "Tools for merchants and agents",
      },
      {
        label: "SACCOs & Institutions",
        path: "/solutions#saccos-institutions",
        icon: <BankOutlined />,
        desc: "Institutional financial services",
      },
      {
        label: "Local Governments",
        path: "/solutions#local-governments",
        icon: <ApartmentOutlined />,
        desc: "Municipal payment systems",
      },
    ],
  },
  {
    label: "Products & Services",
    path: "/products-services",
    children: [
      {
        label: "Pay Bills",
        path: "/products-services#pay-bills",
        icon: <PayCircleOutlined />,
        desc: "Utilities, taxes & more",
      },
      {
        label: "Street Parking",
        path: "/products-services#street-parking",
        icon: <CarOutlined />,
        desc: "Digital parking payments",
      },
      {
        label: "Goods & Services",
        path: "/products-services#goods-services",
        icon: <ShoppingOutlined />,
        desc: "Buy and sell securely",
      },
      {
        label: "Fees & Taxes",
        path: "/products-services#fees-taxes",
        icon: <CalculatorOutlined />,
        desc: "Government fee collection",
      },
      {
        label: "Financial Services",
        path: "/products-services#financial-services",
        icon: <CreditCardOutlined />,
        desc: "Loans, savings & more",
      },
    ],
  },
  {
    label: "Resources",
    path: "/resources",
    children: [
      {
        label: "Overview",
        path: "/resources#overview",
        icon: <ReadOutlined />,
        desc: "Insights and articles",
      },
      {
        label: "Blogs",
        path: "/resources#blogs",
        icon: <HighlightAltOutlined />,
        desc: "Insights and articles",
      },
      {
        label: "Testimonials",
        path: "/resources#testimonials",
        icon: <StarOutlined />,
        desc: "What our clients say",
      },
      {
        label: "Apps",
        path: "/resources#apps",
        icon: <MobileOutlined />,
        desc: "Download our apps",
      },
      {
        label: "FAQ",
        path: "/resources#faq",
        icon: <QuestionCircleOutlined />,
        desc: "Common questions answered",
      },
    ],
  },
  {
    label: "About Us",
    path: "/about",
    children: [
      {
        label: "Who we are",
        path: "/about#about",
        icon: <TeamOutlined />,
        desc: "Our story and team",
      },
      {
        label: "Vision & Mission",
        path: "/about#mission-vision",
        icon: <EyeOutlined />,
        desc: "What drives us forward",
      },
      {
        label: "Our Impact",
        path: "/about#our-impact",
        icon: <PublicOutlined />,
        desc: "Our impact across Uganda",
      },
    ],
  },
  { label: "Contact Us", path: "/contact" },
  { label: "Check Status", path: "/check-status" },
];

const dropdownItems = navLinks.filter((n) => n.children);

const TopBar = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [notchX, setNotchX] = useState(0);
  const [displayedIdx, setDisplayedIdx] = useState<number | null>(null);
  const [animKey, setAnimKey] = useState(0);
  const [direction, setDirection] = useState(1);

  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const flatNavLinks = flattenMenu(navLinks);
  useDynamicPageTitle(flatNavLinks, APP_NAME);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openPanel = useCallback(
    (ddIdx: number) => {
      if (closeTimer.current) clearTimeout(closeTimer.current);

      const newDir = activeIdx === null ? 1 : ddIdx > activeIdx ? 1 : -1;
      setDirection(newDir);
      setActiveIdx(ddIdx);
      setIsOpen(true);

      const el = itemRefs.current[ddIdx];
      if (el) {
        const rect = el.getBoundingClientRect();
        setNotchX(rect.left + rect.width / 2);
      }

      if (contentTimer.current) clearTimeout(contentTimer.current);
      contentTimer.current = setTimeout(() => {
        setDisplayedIdx(ddIdx);
        setAnimKey((k) => k + 1);
      }, 50);
    },
    [activeIdx],
  );

  const scheduleClose = useCallback(() => {
    closeTimer.current = setTimeout(() => {
      setIsOpen(false);
      setActiveIdx(null);
    }, 120);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const activeItem = displayedIdx !== null ? dropdownItems[displayedIdx] : null;

  const buttons = (
    <ButtonsGroup>
      <SignInBtn onClick={() => navigate(URL_LOGIN)}>
        <Link to={URL_LOGIN}>Sign In</Link>
      </SignInBtn>
      <RegisterBtn type="primary" onClick={() => navigate(URL_REGISTER)}>
        <Link to={URL_REGISTER}>Get Started</Link>
      </RegisterBtn>
    </ButtonsGroup>
  );

  return (
    <>
      <Wrapper $scrolled={scrolled}>
        <Inner id="nav">
          <LeftSide>
            <LogoWrap>
              <Link to="/">
                <img src={LogoHorizontalSVG} width={120} alt={APP_NAME} />
              </Link>
            </LogoWrap>

            <LinksRow id="links">
              {navLinks.map((navLink) => {
                const ddIdx = dropdownItems.findIndex(
                  (d) => d.label === navLink.label,
                );
                const hasChildren = ddIdx !== -1;
                const isActive = hasChildren && activeIdx === ddIdx && isOpen;

                return (
                  <NavItem
                    key={navLink.path}
                    ref={
                      hasChildren
                        ? (el) => {
                            itemRefs.current[ddIdx] = el;
                          }
                        : undefined
                    }
                    onMouseEnter={() =>
                      hasChildren ? openPanel(ddIdx) : scheduleClose()
                    }
                    onMouseLeave={hasChildren ? scheduleClose : undefined}
                  >
                    <NavAnchor as={Link} to={navLink.path} $active={isActive}>
                      {navLink.label}
                      {hasChildren && <ChevronIcon $open={isActive} />}
                    </NavAnchor>
                    <NavUnderline $active={isActive} />
                  </NavItem>
                );
              })}
            </LinksRow>
          </LeftSide>

          <div id="links">{buttons}</div>

          <GenericDrawer
            title="Menu"
            placement="right"
            button={
              <MobileToggle id="mobile">
                <CIcon
                  icon={cilMenu}
                  width={20}
                  style={{ color: primaryColor }}
                />
              </MobileToggle>
            }
          >
            <div
              style={{
                padding: "20px 24px 16px",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              {buttons}
            </div>
            <div style={{ padding: "8px 16px" }}>
              {navLinks.map((navLink) => (
                <MobileNavItem key={navLink.path}>
                  <Link to={navLink.path}>{navLink.label}</Link>
                  {(navLink as any).children && (
                    <MobileChildren>
                      {(navLink as any).children.map((child: any) => (
                        <Link key={child.path} to={child.path}>
                          <MobileChildItem>
                            <MobileChildIcon>{child.icon}</MobileChildIcon>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>
                                {child.label}
                              </div>
                              <div style={{ fontSize: 12, color: "#94a3b8" }}>
                                {child.desc}
                              </div>
                            </div>
                          </MobileChildItem>
                        </Link>
                      ))}
                    </MobileChildren>
                  )}
                </MobileNavItem>
              ))}
            </div>
          </GenericDrawer>
        </Inner>
      </Wrapper>

      {/*
        Notch lives OUTSIDE <Wrapper> so it has its own fixed stacking context
        above the panel (z-index 1002 vs panel's 998).
        It renders two triangles: border triangle (panel border colour) + white fill on top.
      */}
      <NotchOuter $x={notchX} $visible={isOpen}>
        {/* border-coloured backing triangle */}
        <NotchBorder />
        {/* white fill sits 1px lower, masking the panel's top border */}
        <NotchFill />
      </NotchOuter>

      {/* Panel backdrop */}
      <PanelBackdrop
        $visible={isOpen}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      >
        <Panel $visible={isOpen}>
          <PanelInner>
            {activeItem?.children && (
              <SlideContent key={animKey} $direction={direction}>
                <ItemGrid $cols={Math.min(activeItem.children.length, 4)}>
                  {activeItem.children.map((child: any) => (
                    <Link
                      key={child.path}
                      to={child.path}
                      onClick={() => {
                        setIsOpen(false);
                        setActiveIdx(null);
                      }}
                    >
                      <Item>
                        <ItemIcon>{child.icon}</ItemIcon>
                        <ItemBody>
                          <ItemLabel>{child.label}</ItemLabel>
                          <ItemDesc>{child.desc}</ItemDesc>
                        </ItemBody>
                        <ItemArrow>
                          <ArrowRightOutlined />
                        </ItemArrow>
                      </Item>
                    </Link>
                  ))}
                </ItemGrid>

                <PanelFooter>
                  <FooterLink
                    to={activeItem.path}
                    onClick={() => {
                      setIsOpen(false);
                      setActiveIdx(null);
                    }}
                  >
                    Explore all {activeItem.label}{" "}
                    <ArrowRightOutlined style={{ fontSize: 11 }} />
                  </FooterLink>
                </PanelFooter>
              </SlideContent>
            )}
          </PanelInner>
        </Panel>
      </PanelBackdrop>
    </>
  );
};

export default TopBar;

/* ─── Keyframes ───────────────────────────────────────────── */
const fromRight = keyframes`
  from { opacity: 0; transform: translateX(16px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const fromLeft = keyframes`
  from { opacity: 0; transform: translateX(-16px); }
  to   { opacity: 1; transform: translateX(0); }
`;

/* ─── Wrapper / nav bar ───────────────────────────────────── */
const Wrapper = styled.nav<{ $scrolled: boolean }>`
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, ${(p) => (p.$scrolled ? 1 : 0.97)});
  backdrop-filter: blur(12px);
  border-bottom: 1px solid ${(p) => (p.$scrolled ? "#e5e7eb" : "#f3f4f6")};
  box-shadow: ${(p) => (p.$scrolled ? "0 1px 12px rgba(0,0,0,0.06)" : "none")};
  transition: all 0.3s ease;

  @media (max-width: 960px) {
    #links {
      display: none;
    }
    #nav {
      justify-content: space-between;
      padding: 0 20px;
    }
  }
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  height: ${NAV_HEIGHT}px;
  padding: 0 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
`;
const LogoWrap = styled.div`
  flex-shrink: 0;
`;
const LinksRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const NavItem = styled.div`
  position: relative;
  height: ${NAV_HEIGHT}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const NavUnderline = styled.div<{ $active: boolean }>`
  position: absolute;
  bottom: 0;
  left: 8px;
  right: 8px;
  height: 2px;
  border-radius: 2px 2px 0 0;
  background: ${blueColor};
  transform: scaleX(${(p) => (p.$active ? 1 : 0)});
  transform-origin: center;
  transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
`;

const ChevronIcon = styled(KeyboardArrowDown)<{ $open: boolean }>`
  font-size: 16px !important;
  transition: transform 0.28s ease;
  vertical-align: middle;
  opacity: 0.5;
  margin-left: 1px;
  transform: ${(p) => (p.$open ? "rotate(180deg)" : "rotate(0)")} !important;
`;

const NavAnchor = styled.span<{ $active?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: ${(p) => (p.$active ? blueColor : "#374151")};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    color 0.2s,
    background 0.2s;
  &:hover {
    color: ${blueColor};
    background: ${blueColor}08;
  }
`;

/* ─── Notch ───────────────────────────────────────────────── */
/*
  Two-layer notch:
    NotchOuter  – positions the notch at the correct X, fixed above panel
    NotchBorder – slightly taller triangle in border colour (#e8edf2)
    NotchFill   – white triangle on top, 1px shorter, masks the border
*/
const NotchOuter = styled.div<{ $x: number; $visible: boolean }>`
  position: fixed;
  top: ${NAV_HEIGHT - 1}px; /* overlap nav bottom border by 1px */
  left: ${(p) => p.$x}px;
  transform: translateX(-50%);
  z-index: 1002; /* above the panel (998) and wrapper (1000) */
  pointer-events: none;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition:
    left 0.32s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.18s ease;
`;

const NotchBorder = styled.div`
  width: 0;
  height: 0;
  border-left: ${NOTCH_W / 2 + 1}px solid transparent;
  border-right: ${NOTCH_W / 2 + 1}px solid transparent;
  border-bottom: ${NOTCH_H + 1}px solid #e8edf2;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
`;

const NotchFill = styled.div`
  width: 0;
  height: 0;
  border-left: ${NOTCH_W / 2}px solid transparent;
  border-right: ${NOTCH_W / 2}px solid transparent;
  border-bottom: ${NOTCH_H}px solid #fff;
  position: absolute;
  top: 1px; /* sits 1px below the border triangle */
  left: 50%;
  transform: translateX(-50%);
`;

/* ─── Panel ───────────────────────────────────────────────── */
const PanelBackdrop = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: ${NAV_HEIGHT}px;
  left: 0;
  right: 0;
  z-index: 998;
  pointer-events: ${(p) => (p.$visible ? "all" : "none")};
`;

const Panel = styled.div<{ $visible: boolean }>`
  background: #fff;
  border-top: 1px solid #e8edf2;
  border-bottom: 1px solid #e8edf2;
  box-shadow:
    0 14px 36px rgba(0, 0, 0, 0.08),
    0 2px 8px rgba(0, 0, 0, 0.04);
  /* Smooth height transition – no abrupt jump */
  max-height: ${(p) => (p.$visible ? "380px" : "0")};
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  overflow: hidden;
  transition:
    max-height 0.38s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.22s ease;
`;

const PanelInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 28px 32px 20px;
  overflow: hidden;
`;

/* Slide animation — Stripe-paced (0.52s) with soft easing */
const SlideContent = styled.div<{ $direction: number }>`
  animation: ${(p) => (p.$direction >= 0 ? fromRight : fromLeft)} 0.52s
    cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
`;

/* ─── Item grid ───────────────────────────────────────────── */
const ItemGrid = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols}, 1fr);
  gap: 4px;
  margin-bottom: 16px;
  max-width: min(100%, ${(p) => p.$cols * 290}px);

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    max-width: 100%;
  }
`;

const ItemArrow = styled.span`
  font-size: 11px;
  color: #cbd5e1;
  opacity: 0;
  transform: translateX(-4px);
  transition:
    opacity 0.2s,
    transform 0.2s,
    color 0.2s;
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 4px;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.18s;

  &:hover {
    background: ${blueColor}09;
    ${ItemArrow} {
      opacity: 1;
      transform: translateX(0);
      color: ${blueColor};
    }
  }
`;

const ItemIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 9px;
  background: ${blueColor}0d;
  border: 1px solid ${blueColor}1c;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${blueColor};
  font-size: 16px;
  flex-shrink: 0;
  transition:
    background 0.18s,
    color 0.18s,
    border-color 0.18s;

  ${Item}:hover & {
    background: ${blueColor};
    border-color: ${blueColor};
    color: #fff;
  }
`;

const ItemBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.18s;
  ${Item}:hover & {
    color: ${blueColor};
  }
`;

const ItemDesc = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ─── Panel footer ────────────────────────────────────────── */
const PanelFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
`;

const FooterLink = styled(Link)`
  font-size: 13px;
  font-weight: 600;
  color: ${blueColor};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 5px;
  transition:
    gap 0.18s,
    color 0.18s;
  &:hover {
    color: ${goldColor};
    gap: 8px;
  }
`;

/* ─── Buttons ─────────────────────────────────────────────── */
const ButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  @media (max-width: 960px) {
    flex-direction: column;
    width: 100%;
  }
`;

const SignInBtn = styled(Button)`
  height: 36px;
  padding: 0 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  border-color: #e5e7eb !important;
  color: #374151;
  background: transparent;
  a {
    color: inherit;
    text-decoration: none;
  }
  &:hover {
    border-color: ${blueColor} !important;
    color: ${blueColor} !important;
  }
`;

const RegisterBtn = styled(Button)`
  height: 36px;
  padding: 0 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  background: ${blueColor} !important;
  border: none !important;
  box-shadow: 0 2px 8px ${blueColor}40 !important;
  a {
    color: #fff;
    text-decoration: none;
  }
  &:hover {
    opacity: 0.9 !important;
    box-shadow: 0 4px 14px ${blueColor}50 !important;
    transform: translateY(-1px);
  }
`;

const MobileToggle = styled.div`
  height: 38px;
  width: 38px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
  @media (max-width: 960px) {
    display: flex;
  }
`;

/* ─── Mobile nav ──────────────────────────────────────────── */
const MobileNavItem = styled.div`
  border-bottom: 1px solid #f5f5f5;
  & > a {
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
    padding: 10px 8px;
    text-decoration: none;
  }
`;
const MobileChildren = styled.div`
  padding: 0 0 8px 8px;
`;
const MobileChildItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  transition: background 0.15s;
  &:hover {
    background: ${blueColor}0a;
  }
`;
const MobileChildIcon = styled.span`
  color: ${blueColor};
  font-size: 14px;
  width: 28px;
  height: 28px;
  background: ${blueColor}0d;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

// import styled from "styled-components";
// import { blueColor, primaryColor } from "../../../styled/Theme";
// import { Button } from "antd";
// import { Link, useNavigate } from "react-router-dom";
// import CIcon from "@coreui/icons-react";
// import { cilMenu } from "@coreui/icons";
// import { KeyboardArrowDown } from "@mui/icons-material";
// import { URL_LOGIN, URL_REGISTER } from "../../../../utils/urls";
// import { GenericDrawer } from "../../../common/GenericDrawer";
// import { LogoHorizontalSVG } from "../../../../assets/svg/SvgIcons";
// import { APP_NAME } from "../../../../utils/constants";
// import { useDynamicPageTitle } from "../../../../hooks/dynamic_title/useDynamicPageTitle";
// import { flattenMenu } from "../../../../utils/menuUtils";

// const navLinks = [
//   { label: "Home", path: "/" },

//   {
//     label: "Solutions",
//     path: "/solutions",
//     children: [
//       { label: "Individuals", path: "/solutions/individuals" },
//       { label: "Agents & Merchants", path: "/solutions/agents-merchants" },
//       {
//         label: "SACCOs & Institutions",
//         path: "/solutions/saccos-institutions",
//       },
//       {
//         label: "Local Governments & Municipalities",
//         path: "/solutions/local-governments-municipalities",
//       },
//     ],
//   },

//   {
//     label: "Products & Services",
//     path: "/products-services",
//     children: [
//       { label: "Pay Bills", path: "/products-services/pay-bills" },
//       {
//         label: "Street Parking",
//         path: "/products-services/street-parking",
//       },
//       {
//         label: "Goods & Services",
//         path: "/products-services/goods-services",
//       },
//       {
//         label: "Fees & Taxes",
//         path: "/products-services/fees-taxes",
//       },
//       {
//         label: "Financial Services",
//         path: "/products-services/financial-services",
//       },
//     ],
//   },

//   {
//     label: "Resources",
//     path: "/resources",
//     children: [
//       { label: "Blogs", path: "/resources/blogs" },
//       { label: "Testimonials", path: "/resources/testimonials" },
//       { label: "Apps", path: "/resources/apps" },
//       { label: "FAQ", path: "/resources/faq" },
//     ],
//   },
//   {
//     label: "About Us",
//     path: "/about",
//     children: [
//       { label: "Who we are", path: "/about" },
//       { label: "Vision & Mission", path: "/about/vision-mission" },
//     ],
//   },
//   { label: "Contact Us", path: "/contact" },
//   { label: "Check Status", path: "/check-status" },
// ];

// type TNavLink = {
//   data: any;
//   className?: string;
// };

// const NavLink = ({ data, className }: TNavLink) => {
//   const { label, path, children } = data || {};
//   return (
//     <NavLinkWrapper className="pointer animate dropdown">
//       <Link to={path} className={className}>
//         {label} {children && <KeyboardArrowDown />}
//       </Link>

//       {children && (
//         <div className="dropdown-content animate">
//           {children.map(({ label, path }) => (
//             <Link key={path} to={path} className="animate">
//               {label}
//             </Link>
//           ))}
//         </div>
//       )}
//     </NavLinkWrapper>
//   );
// };

// const TopBar = () => {
//   const navigate = useNavigate();

//   const flatNavLinks = flattenMenu(navLinks);
//   useDynamicPageTitle(flatNavLinks, APP_NAME);

//   const buttons = (
//     <ButtonsGroup>
//       {/* REGISTER */}
//       <Button
//         type="primary"
//         style={{ backgroundColor: blueColor }}
//         className="radius30"
//         onClick={() => navigate("/register")}
//       >
//         <Link to={URL_REGISTER}>Create an Account</Link>
//       </Button>
//       <Button
//         type="primary"
//         style={{ backgroundColor: `${blueColor}` }}
//         className="radius30"
//       >
//         <Link to={URL_LOGIN}>Sign In</Link>
//       </Button>
//     </ButtonsGroup>
//   );

//   return (
//     <Wrapper className="flexCenter">
//       <div className="flexCenter container" id="nav">
//         {/* LINKS */}
//         <div className="flexSpaceCenter x100">
//           <div className="flexNullCenter" style={{ gap: 40 }}>
//             {/* LOGO */}
//             <div id="logo">
//               <Link to="/">
//                 <img src={LogoHorizontalSVG} width={130} />
//               </Link>
//             </div>
//             {/* LINKS */}
//             <div className="flexCenter gap30" id="links">
//               {navLinks.map((navLink) => (
//                 <NavLink key={navLink.path} data={navLink} className="black" />
//               ))}
//             </div>
//           </div>

//           {/* BUTTONS */}
//           <div id="links">{buttons}</div>
//         </div>

//         {/* MOBILE NAV */}
//         <GenericDrawer
//           title="Menu"
//           placement="right"
//           button={
//             <MobileNavWrapper id="mobile" className="flexCenter">
//               <CIcon
//                 icon={cilMenu}
//                 width={20}
//                 style={{ color: primaryColor }}
//               />
//             </MobileNavWrapper>
//           }
//         >
//           <div
//             className="flexCenter gap20"
//             style={{ paddingBlock: 30, borderBottom: "1px solid #eee" }}
//           >
//             {buttons}
//           </div>
//           <div style={{ padding: "0 30px" }}>
//             {navLinks.map((navLink) => (
//               <NavLink key={navLink.path} data={navLink} className="black" />
//             ))}
//           </div>
//         </GenericDrawer>
//       </div>
//     </Wrapper>
//   );
// };

// export default TopBar;

// const MobileNavWrapper = styled.div`
//   height: 40px;
//   width: 40px;
//   border: 1px solid #ffffff50;
//   border-radius: 10px;
//   display: none;

//   @media (max-width: 960px) {
//     display: flex;
//   }
// `;

// const NavLinkWrapper = styled.a`
//   font-weight: bold;
//   font-size: 14px;
//   text-decoration: none;
//   padding: 20px 0;
//   position: relative;
//   color: #000;
//   cursor: pointer;
//   transition: all 0.3s ease;

//   &:hover {
//     color: ${primaryColor};
//     transform: translateY(-2px);
//   }

//   .dropdown-content {
//     display: none;
//     position: absolute;
//     top: 100%;
//     left: 0;
//     min-width: 180px;
//     background-color: #fff;
//     box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.1);
//     border-radius: 6px;
//     overflow: hidden;
//     opacity: 0;
//     transform: translateY(10px);
//     transition:
//       opacity 0.3s ease,
//       transform 0.3s ease;
//     z-index: 1000;
//   }

//   .dropdown-content a {
//     display: block;
//     padding: 10px 20px;
//     color: #333;
//     font-size: 14px;
//     text-decoration: none;
//     transition: all 0.3s ease;
//   }

//   .dropdown-content a:hover {
//     background-color: ${blueColor}20;
//     color: ${blueColor};
//     transform: translateX(5px);
//   }

//   &:hover .dropdown-content {
//     display: block;
//     opacity: 1;
//     transform: translateY(0);
//   }

//   svg {
//     transition: transform 0.3s ease;
//   }

//   &:hover svg {
//     transform: rotate(180deg);
//   }
// `;

// const ButtonsGroup = styled.div`
//   display: flex;
//   align-items: center;
//   gap: 10px;

//   button {
//     transition: all 0.3s ease;
//     &:hover {
//       transform: translateY(-2px);
//       box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
//     }
//   }

//   @media (max-width: 960px) {
//     flex-direction: column;
//     width: 100%;
//   }
// `;

// const Wrapper = styled.nav`
//   backdrop-filter: blur(8px);
//   background-color: rgba(255, 255, 255, 0.95);
//   width: 100%;
//   height: 60px;
//   position: fixed;
//   border-bottom: 1px solid #eee;
//   top: 0;
//   left: 0;
//   z-index: 999;
//   transition: all 0.3s ease;

//   &:hover {
//     background-color: rgba(255, 255, 255, 1);
//   }

//   @media (max-width: 960px) {
//     #links {
//       display: none;
//     }

//     #nav {
//       justify-content: space-between;
//       padding: 0 30px;
//     }
//   }
// `;
